import { type SkinDefinition } from "@common/definitions/items/skins";
import { Vec, type Vector } from "@common/utils/vector";
import { type ColorSource, type Container, Graphics, Rectangle, Sprite, Texture } from "pixi.js";
import { Game } from "../game";
import { markNoNormal } from "../managers/normalLook";
import { ShadowLook } from "../managers/shadowLook";
import { type TrenchioSprite } from "../utils/pixi";

/** one shared shadow texture: the body's own circle with a soft 7 px edge, drawn offset down-right as a small, clear crescent */
let shadowTexture: Texture | undefined;
const getShadowTexture = (): Texture => {
    if (shadowTexture) return shadowTexture;
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = 128;
    const ctx = canvas.getContext("2d");
    if (ctx) {
        const gradient = ctx.createRadialGradient(64, 64, 0, 64, 64, 64);
        gradient.addColorStop(0, "rgba(0, 0, 0, 0.5)");
        gradient.addColorStop(0.86, "rgba(0, 0, 0, 0.5)");
        gradient.addColorStop(1, "rgba(0, 0, 0, 0)");
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, 128, 128);
    }
    shadowTexture = Texture.from(canvas);
    return shadowTexture;
};

/**
 * The gloves' knuckle lines and thumb (for a left and a right hand) and their mud, drawn once into textures every
 * player shares: as shapes, they'd be several hundred vertices per hand to batch every frame; as sprites, four.
 * Each comes with where its texture's corner sits in the hand's own space.
 */
interface GlovePart { readonly texture: Texture, readonly x: number, readonly y: number }
let gloveParts: { readonly detail: readonly [GlovePart, GlovePart], readonly mud: GlovePart } | undefined;
const getGloveParts = (): typeof gloveParts & object => {
    if (gloveParts) return gloveParts;
    const bake = (draw: (g: Graphics) => void): GlovePart => {
        const g = new Graphics();
        draw(g);
        // a whole-pixel frame a little larger than the shape (the texture is cut to whole pixels: nothing is lost)
        const b = g.getLocalBounds();
        const frame = new Rectangle(Math.floor(b.minX) - 1, Math.floor(b.minY) - 1, Math.ceil(b.maxX - b.minX) + 3, Math.ceil(b.maxY - b.minY) + 3);
        // drawn at three times the size, so the lines stay smooth when the hand is scaled or turned
        const texture = Game.pixi.renderer.generateTexture({ target: g, frame, resolution: 3, antialias: true });
        g.destroy();
        return { texture, x: frame.x, y: frame.y };
    };
    // knuckles on the front, a thumb on the inner side (towards the other hand); outlines only, so any skin's hand shows through
    const detail = (inner: number): GlovePart => bake(g => g
        .moveTo(5, -9).quadraticCurveTo(11, -3, 11, 4).stroke({ width: 2.4, color: 0x000000, alpha: 0.5, cap: "round" })
        .moveTo(9, -11).quadraticCurveTo(15, -5, 15, 2).stroke({ width: 2.4, color: 0x000000, alpha: 0.42, cap: "round" })
        .ellipse(-3, 11 * inner, 6.5, 4.5).stroke({ width: 2.6, color: 0x000000, alpha: 0.55 })
        .moveTo(-8, -6).quadraticCurveTo(-4, -12, 3, -12).stroke({ width: 2, color: 0xffffff, alpha: 0.18, cap: "round" }));
    const mud = bake(g => g.circle(-4, -3, 4.2).fill(0x3b2e1c).circle(5, 5, 3).fill(0x4a3a22).circle(2, -8, 2).fill(0x2e2415));
    return gloveParts = { detail: [detail(1), detail(-1)], mud };
};
const glovePart = (part: GlovePart): Sprite => {
    const sprite = new Sprite(part.texture);
    sprite.position.set(part.x, part.y);
    return sprite;
};

/** khaki wool, for skins that don't set their own colour */
const DEFAULT_SLEEVE = 0x9a8757;
/** the shoulder each hand's sleeve starts from (the left hand rests at y = -35, the right at y = +35) */
const LEFT_SHOULDER = Vec(6, -34);
const RIGHT_SHOULDER = Vec(6, 34);
/** the circle and its vest ring: anything inside this radius is hidden under them anyway */
const HIDDEN_RADIUS = 54;
/** game units walked for a full coat of mud */
const MUD_DISTANCE = 1800;

const darken = (color: number, amount: number): number => {
    const r = (color >> 16) & 255, g = (color >> 8) & 255, b = color & 255;
    const k = 1 - amount;
    return (Math.round(r * k) << 16) | (Math.round(g * k) << 8) | Math.round(b * k);
};

const toNumber = (color: ColorSource | undefined): number | undefined => typeof color === "number" ? color : undefined;

/** what a body keeps of the living player's gear (see gore.ts): the sleeve colours, the mud and the kill chevrons */
export interface GearLook {
    readonly sleeve: number
    readonly edge: number
    readonly cuff: number
    readonly travel: number
    readonly kills: number
}

/**
 * What a player wears around the circle, without touching it: sleeves from under the vest to each hand,
 * glove details on the hands, a soft contact shadow, mud that builds up over a match, and chevrons on the
 * forearm for kills. The circle (and the emblem drawn on it), the helmet, vest and backpack are untouched.
 */
export class PlayerGear {
    private readonly _sleeves = new Graphics();
    private readonly _shadow = new Sprite(getShadowTexture());
    private readonly _gloves: Array<{ readonly fist: TrenchioSprite, readonly detail: Sprite, readonly mud: Sprite }>;

    private _sleeve = darken(DEFAULT_SLEEVE, 0.34);
    private _edge = darken(DEFAULT_SLEEVE, 0.78);
    private _cuff = darken(DEFAULT_SLEEVE, 0.5);

    private _travel = 0;
    private _mud = 0;
    private _last?: Vector;
    /**
     * what the sleeves were last drawn from: they're only redrawn when the hands move, or the mud, kills or colours
     * change (rebuilding the shapes every frame for every player on screen costs time, and the hands are usually still)
     */
    private readonly _drawn = { lx: NaN, ly: NaN, rx: NaN, ry: NaN, lv: false, rv: false, kills: -1, mud: -1, sleeve: -1 };

    /** kills this match (chevrons: 1, 2, or 3 gold) */
    kills = 0;

    constructor(container: Container, leftFist: TrenchioSprite, rightFist: TrenchioSprite) {
        this._sleeves.zIndex = -0.5; // above the backpack (-1), under the vest, body and hands
        // drawn in the same batch as the sprites around it (Pixi would otherwise give it a draw call of its own)
        this._sleeves.context.batchMode = "batch";
        // just a little bigger than the body (90 px), so only a thin soft crescent shows past it
        this._shadow.anchor.set(0.5);
        this._shadow.zIndex = -2;
        // (0.98.7: a shadow has no shape of its own in the lighting's picture: managers/normalLook.ts)
        markNoNormal(this._shadow);
        this._shadow.width = this._shadow.height = 102;
        container.addChild(this._shadow, this._sleeves);

        const parts = getGloveParts();
        this._gloves = [leftFist, rightFist].map((fist, i) => {
            const detail = glovePart(parts.detail[i]);
            const mud = glovePart(parts.mud);
            mud.alpha = 0;
            fist.addChild(detail, mud);
            return { fist, detail, mud };
        });
    }

    /** sleeves take the skin's colour (its body tint, else its backpack or hand tint, else khaki), a third darker so arms read against the body */
    setSkin(skin: SkinDefinition, fistTint: ColorSource): void {
        const hand = toNumber(fistTint);
        const tint = (skin.grassTint ? hand : undefined) ?? skin.baseTint ?? skin.backpackTint ?? (hand !== 0xffffff ? hand : undefined) ?? DEFAULT_SLEEVE;
        this._sleeve = darken(tint, 0.34);
        this._edge = darken(tint, 0.78);
        this._cuff = darken(tint, 0.5);
    }

    get look(): GearLook {
        return { sleeve: this._sleeve, edge: this._edge, cuff: this._cuff, travel: this._travel, kills: this.kills };
    }

    /** dress this gear like a living player's (a body lying where they fell) */
    wear(look: GearLook): void {
        this._sleeve = look.sleeve;
        this._edge = look.edge;
        this._cuff = look.cuff;
        this._travel = look.travel;
        this._mud = Math.min(1, look.travel / MUD_DISTANCE);
        this.kills = look.kills;
    }

    /** every frame: follow the hands, keep the shadow falling the same way, gather mud */
    update(options: {
        readonly position: Vector
        readonly rotation: number
        readonly visible: boolean
        readonly inWater: boolean
        readonly leftFist: TrenchioSprite
        readonly rightFist: TrenchioSprite
        /** how much of the contact shadow shows (1, the default; a body lying flat on the ground has none: see gore.ts) */
        readonly shadow?: number
        /** in the air (1.06, jumping in): the flight isn't walking, no mud from it */
        readonly airborne?: boolean
    }): void {
        const { position, rotation, visible, inWater, leftFist, rightFist, shadow = 1, airborne = false } = options;
        this._sleeves.visible = visible;
        // (0.98.6: the contact shadow goes with Settings → Shadows at Off; the cast shadow is managers/shadowLook.ts's)
        this._shadow.visible = visible && shadow > 0 && ShadowLook.contact;
        this._shadow.alpha = shadow;
        if (!visible) return;

        // mud: builds with distance walked, washes off in water
        if (this._last && !airborne) {
            const moved = Vec.len(Vec.sub(position, this._last));
            if (moved < 20) this._travel += moved;
        }
        this._last = Vec.clone(position);
        if (inWater) this._travel = Math.max(0, this._travel - 12);
        this._mud = Math.min(1, this._travel / MUD_DISTANCE);
        for (const glove of this._gloves) {
            glove.mud.alpha = this._mud * 0.85;
            glove.detail.visible = glove.fist.visible;
        }

        // the shadow falls down and to the right on screen, whichever way the player faces (a contact shadow, a thin
        // crescent past the body: the long one the sun throws is managers/shadowLook.ts's, 0.98.6)
        const c = Math.cos(-rotation), s = Math.sin(-rotation);
        this._shadow.position.set(6 * c - 9 * s, 6 * s + 9 * c);

        // mud shows in steps of 5% (it builds up slowly), so walking doesn't redraw the sleeves every frame
        const mud = Math.round(this._mud * 20) / 20;
        const drawn = this._drawn;
        if (
            drawn.lx === leftFist.position.x && drawn.ly === leftFist.position.y && drawn.lv === leftFist.visible
            && drawn.rx === rightFist.position.x && drawn.ry === rightFist.position.y && drawn.rv === rightFist.visible
            && drawn.kills === this.kills && drawn.mud === mud && drawn.sleeve === this._sleeve
        ) return;
        drawn.lx = leftFist.position.x; drawn.ly = leftFist.position.y; drawn.lv = leftFist.visible;
        drawn.rx = rightFist.position.x; drawn.ry = rightFist.position.y; drawn.rv = rightFist.visible;
        drawn.kills = this.kills; drawn.mud = mud; drawn.sleeve = this._sleeve;

        const g = this._sleeves.clear();
        for (const [fist, shoulder] of [[leftFist, LEFT_SHOULDER], [rightFist, RIGHT_SHOULDER]] as const) {
            if (!fist.visible) continue;
            const hand = Vec(fist.position.x, fist.position.y);
            const reach = Vec.sub(hand, shoulder);
            const length = Vec.len(reach);
            if (length < 1) continue;
            // an arm of steady width, bowing slightly away from the body
            const wrist = Vec.sub(hand, Vec.scale(reach, 11 / length));
            const mid = Vec.scale(Vec.add(shoulder, wrist), 0.5);
            const out = Vec.scale(Vec(-reach.y, reach.x), (shoulder.y < 0 ? -1 : 1) * 5 / length);
            const elbow = Vec.add(mid, out);
            // flat-ended strokes with a round end drawn at the wrist (the shoulder end is under the body): round caps
            // would be most of the shape's vertices
            g.moveTo(shoulder.x, shoulder.y).quadraticCurveTo(elbow.x, elbow.y, wrist.x, wrist.y).stroke({ width: 20, color: this._edge });
            g.circle(wrist.x, wrist.y, 10).fill(this._edge);
            g.moveTo(shoulder.x, shoulder.y).quadraticCurveTo(elbow.x, elbow.y, wrist.x, wrist.y).stroke({ width: 15, color: this._sleeve });
            g.circle(wrist.x, wrist.y, 7.5).fill(this._sleeve);
            const cuff = Vec.sub(hand, Vec.scale(reach, 16 / length));
            g.circle(cuff.x, cuff.y, 7.5).fill(this._cuff).stroke({ width: 2, color: this._edge });

            // mud splashes along the sleeve
            if (mud > 0.05) {
                for (const [t, r] of [[0.55, 4.5], [0.78, 3.4]] as const) {
                    const p = this._alongCurve(shoulder, elbow, wrist, t);
                    if (Vec.len(p) > HIDDEN_RADIUS - 4) g.circle(p.x + 2, p.y - 1, r).fill({ color: 0x3b2e1c, alpha: 0.85 * mud });
                }
            }

            // chevrons on the forearm, where the sleeve shows past the vest: gold at 3 kills or more
            if (this.kills > 0) {
                const p = this._alongCurve(shoulder, elbow, wrist, 0.7);
                if (Vec.len(p) > HIDDEN_RADIUS) {
                    const gold = this.kills >= 3;
                    const along = Vec.sub(wrist, shoulder); const al = Vec.len(along) || 1;
                    const dir = Vec.scale(along, 1 / al), side = Vec(-dir.y, dir.x);
                    for (let k = 0; k < Math.min(3, this.kills); k++) {
                        const base = Vec.add(p, Vec.scale(dir, (k - 1) * 6));
                        const tip = Vec.add(base, Vec.scale(dir, 6));
                        const a = Vec.add(base, Vec.scale(side, 7)), b = Vec.sub(base, Vec.scale(side, 7));
                        g.moveTo(a.x, a.y).lineTo(tip.x, tip.y).lineTo(b.x, b.y).stroke({ width: 5.5, color: 0x1d1a14, cap: "round", join: "round" });
                        g.moveTo(a.x, a.y).lineTo(tip.x, tip.y).lineTo(b.x, b.y).stroke({ width: 3, color: gold ? 0xf0c84e : 0xf2ead6, cap: "round", join: "round" });
                    }
                }
            }
        }
    }

    private _alongCurve(a: Vector, control: Vector, b: Vector, t: number): Vector {
        const u = 1 - t;
        return Vec(u * u * a.x + 2 * u * t * control.x + t * t * b.x, u * u * a.y + 2 * u * t * control.y + t * t * b.y);
    }
}
